import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import { CircularProgress } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

import SearchSelect, { OptionItem } from "../../atoms/SearchSelect/SearchSelect";
import { findAllAcoesQualificacao, findAllTurmasAcao } from "../../../API/apiAcoes";
import { findRelatorioPesquisaTotalizadores } from "../../../API/apiRelatoriosTurma";
import { AcaoQualificacao, TurmaAcao } from "../../../types/acao";
import { PesquisaTotalizadoresData } from "../../../types/relatoriosTurma";

import styles from "./ModelosPesquisa.module.css";
import stylesFilter from "../../../styles/Filter.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesTable from "../../../styles/Table.module.css";
import stylesGeneral from "../../../styles/General.module.css";

const chartPalette = ["#5A81FA", "#7C93FF", "#59C3C3", "#F6BD60", "#F28482", "#84A59D", "#9B8AFB", "#4DB6AC"];
const escalaColorMap: Record<string, string> = {
  "1": "#D32F2F",
  "2": "#F57C00",
  "3": "#FBC02D",
  "4": "#7CB342",
  "5": "#2E7D32",
};

const getChartColors = (pergunta: PesquisaTotalizadoresData["perguntas"][number]) => {
  if (pergunta.tipo === "escala") {
    return (pergunta.respostas || []).map((item) => escalaColorMap[String(item.resposta ?? "")] || "#5A81FA");
  }

  return (pergunta.respostas || []).map((_, index) => chartPalette[index % chartPalette.length]);
};

const escalaCincoValores: Record<string, number> = {
  pessimo: 1,
  ruim: 2,
  regular: 3,
  bom: 4,
  otimo: 5,
};

const getEscalaCincoValue = (resposta: { resposta: string | number | null; resposta_texto: string }) => {
  const numericValue = Number(resposta.resposta);
  if (Number.isFinite(numericValue) && numericValue >= 1 && numericValue <= 5) {
    return numericValue;
  }

  const label = String(resposta.resposta_texto || resposta.resposta || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

  return escalaCincoValores[label] ?? null;
};

const getAverageOutOfTen = (pergunta: PesquisaTotalizadoresData["perguntas"][number]): number | null => {
  if (pergunta.tipo !== "escala" && pergunta.tipo !== "escala_dez") {
    return null;
  }

  const somaPonderada = (pergunta.respostas || []).reduce((total, resposta) => {
    const nota =
      pergunta.tipo === "escala_dez" ? Number(resposta.resposta) : getEscalaCincoValue(resposta);
    const quantidade = Number(resposta.total) || 0;
    const isValidScaleValue =
      pergunta.tipo === "escala_dez"
        ? Number.isFinite(nota) && nota >= 0 && nota <= 10
        : nota !== null;

    return isValidScaleValue && quantidade > 0 ? total + nota * quantidade : total;
  }, 0);
  const totalRespostas = Number(pergunta.total_respostas) || 0;

  if (totalRespostas <= 0) {
    return null;
  }

  const average = somaPonderada / totalRespostas;
  return pergunta.tipo === "escala" ? average * 2 : average;
};

const RelatorioTotalizadoresPesquisa = () => {
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingTotalizadores, setLoadingTotalizadores] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [acoes, setAcoes] = useState<AcaoQualificacao[]>([]);
  const [turmas, setTurmas] = useState<TurmaAcao[]>([]);
  const [acaoId, setAcaoId] = useState<number | null>(null);
  const [turmaId, setTurmaId] = useState<number | null>(null);
  const [totalizadores, setTotalizadores] = useState<PesquisaTotalizadoresData | null>(null);

  useEffect(() => {
    const timer = msgError ? setTimeout(() => setMsgError(null), 5000) : undefined;
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [msgError]);

  useEffect(() => {
    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAllAcoesQualificacao(1, 999999);
        setAcoes(response.acoes || []);
      } catch {
        setMsgError("Nao foi possivel carregar as acoes.");
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, []);

  useEffect(() => {
    const loadTurmas = async () => {
      if (!acaoId) {
        setTurmas([]);
        setTurmaId(null);
        return;
      }

      try {
        setLoadingTurmas(true);
        const result = await findAllTurmasAcao(String(acaoId));
        setTurmas(result || []);
      } catch {
        setMsgError("Nao foi possivel carregar as turmas da acao.");
        setTurmas([]);
      } finally {
        setLoadingTurmas(false);
      }
    };

    setTurmaId(null);
    setTotalizadores(null);
    void loadTurmas();
  }, [acaoId]);

  useEffect(() => {
    if (!turmaId) {
      setTotalizadores(null);
      return;
    }

    const loadTotalizadores = async () => {
      try {
        setLoadingTotalizadores(true);
        const response = await findRelatorioPesquisaTotalizadores(turmaId);
        setTotalizadores(response);
      } catch {
        setMsgError("Nao foi possivel carregar os totalizadores da pesquisa.");
      } finally {
        setLoadingTotalizadores(false);
      }
    };

    void loadTotalizadores();
  }, [turmaId]);

  const acaoOptions = useMemo<OptionItem[]>(
    () => acoes.map((acao) => ({ id: acao.id, nome: acao.nome })),
    [acoes],
  );

  const turmaOptions = useMemo<OptionItem[]>(
    () => turmas.map((turma) => ({ id: turma.id, nome: turma.nome })),
    [turmas],
  );

  const renderChart = (pergunta: PesquisaTotalizadoresData["perguntas"][number]) => {
    const totalRespostas = pergunta.total_respostas || 0;
    const averageOutOfTen = getAverageOutOfTen(pergunta);
    const series = [{ name: "Respostas", data: (pergunta.respostas || []).map((item) => item.total) }];
    const options: ApexOptions = {
      chart: { toolbar: { show: false } },
      xaxis: {
        categories: (pergunta.respostas || []).map((item) => item.resposta_texto),
        labels: { style: { fontFamily: "Poppins, sans-serif" } },
      },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
      dataLabels: { enabled: false },
      colors: getChartColors(pergunta),
      tooltip: {
        y: {
          formatter: (value: number) => {
            const percentual = totalRespostas > 0 ? ((value / totalRespostas) * 100).toFixed(1) : "0.0";
            return `${value} resposta(s) - ${percentual}%`;
          },
        },
      },
      grid: { strokeDashArray: 4 },
    };

    return (
      <div key={pergunta.pergunta_id} className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <div className={styles.chartTitle}>
            {pergunta.ordem}. {pergunta.pergunta}
          </div>
          <div className={styles.chartMeta}>
            Total de respostas: {pergunta.total_respostas}
            {averageOutOfTen !== null
              ? ` | Média: ${averageOutOfTen.toLocaleString("pt-BR", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 2,
                })} / 10`
              : ""}
          </div>
        </div>

        <ReactApexChart options={options} series={series} type="bar" height={340} />
      </div>
    );
  };

  return (
    <div className={styles.card}>
      <div className={styles.title}>Totalizadores da Pesquisa</div>

      {msgError ? (
        <div className={stylesTable.message}>
          <Alert severity="error">{msgError}</Alert>
        </div>
      ) : null}

      <div className={stylesGeneral.mt4}>
        <div className={stylesFilter.container}>
          <div className={stylesFilter.grid12}>
            <div className={stylesFilter.inputGroup6}>
              <label className={stylesInput.titleInput}>Ação</label>
              <SearchSelect
                label=""
                options={acaoOptions}
                value={acaoId}
                onChange={(id) => setAcaoId(id)}
                disabled={loadingAcoes}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>

            <div className={stylesFilter.inputGroup6}>
              <label className={stylesInput.titleInput}>Turma</label>
              <SearchSelect
                label=""
                options={turmaOptions}
                value={turmaId}
                onChange={(id) => setTurmaId(id)}
                disabled={!acaoId || loadingTurmas}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>

          </div>
        </div>
      </div>

      {!turmaId ? (
        <div className={stylesGeneral.mt4}>
          <Alert severity="info">Selecione uma acao e uma turma para visualizar os relatorios disponiveis.</Alert>
        </div>
      ) : (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={`${stylesGeneral.textAlignCenter} ${stylesGeneral.wFull}`}>
              <div className={styles.sectionTitle}>Relatorio - Totalizadores da Pesquisa</div>
              <div className={styles.sectionSubtitle}>
                Visualize os totais por pergunta com apoio de graficos em barra.
              </div>
            </div>
          </div>

          {loadingTotalizadores ? (
            <div className={styles.loader}>
              <CircularProgress />
            </div>
          ) : totalizadores ? (
            <>
              <Alert severity="info" className={styles.chartSummary}>
                Total de participantes respondentes: {totalizadores.total_participantes_respondentes}
              </Alert>

              <div className={styles.chartGrid}>
                {totalizadores.perguntas.length > 0 ? (
                  totalizadores.perguntas.map((pergunta) => renderChart(pergunta))
                ) : (
                  <Alert severity="info">Nao ha totalizadores disponiveis para esta turma.</Alert>
                )}
              </div>
            </>
          ) : (
            <Alert severity="info">Nao ha totalizadores disponiveis para esta turma.</Alert>
          )}
        </section>
      )}
    </div>
  );
};

export default RelatorioTotalizadoresPesquisa;
